import {useMemo, useState} from 'react';
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from '@headlessui/react';
import {InformationCircleIcon, XMarkIcon} from '@heroicons/react/24/outline';
import {PrimaryButton} from '../../../../../components/general/PrimaryButton.tsx';
import {SecondaryButton} from '../../../../../components/general/SecondaryButton.tsx';
import {InfoTooltip} from '../../../../../components/general/InfoTooltip.tsx';
import Dropdown, {Option} from '../../../../../components/general/Dropdown.tsx';
import {
    CreditCardSchemeLogo,
    CreditCardScheme,
    PREFIX_LENGTH_BY_SCHEME,
    MAX_PREFIX_LENGTH,
    LAST_FOUR_LENGTH,
    SCHEME_DISPLAY_NAMES,
    PLACEHOLDER_PREFIX_BY_SCHEME,
    detectSchemeFromCreditCardNumber,
    stripDigits,
} from '../../../../../components/specific/CreditCard';
import {BackendAPITypes} from '../../../../../generated/blueprint.ts';
import type {CustomerBankAccount} from '../../../../../api/customerBankAccounts/CustomerBankAccount.ts';
import {useNotifications} from '../../../../../lib/NotificationContext.tsx';
import {cn} from '../../../../../lib/utils.ts';
import {fieldVariants} from '../../../../../components/general/fieldStyles.ts';

type AddCreditCardPayload = BackendAPITypes.BACKENDPreHandleSyncApiV1BankAccountAddCreditCardPostRequestBody;

export interface Institution {
    id: string;
    name: string;
    logo?: string;
}

interface Props {
    open: boolean;
    institutions: Institution[];
    existingCreditCards: CustomerBankAccount[];
    onClose: () => void;
    onSubmit: (payload: AddCreditCardPayload) => Promise<void>;
}

const EMPTY_INSTITUTION_OPTION: Option = {value: '', label: 'Selecteer bank / uitgever'};
const EMPTY_SCHEME_OPTION: Option = {value: '', label: 'Selecteer kaarttype'};
const SCHEME_OPTIONS: Option[] = Object.values(CreditCardScheme).map((scheme) => ({
    value: scheme,
    label: SCHEME_DISPLAY_NAMES[scheme],
}));

export function AddCreditCardModal({open, institutions, existingCreditCards, onClose, onSubmit}: Props) {
    const [name, setName] = useState('');
    const [prefix, setPrefix] = useState('');
    const [suffix, setSuffix] = useState('');
    const [selectedInstitutionId, setSelectedInstitutionId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [prefixUnknown, setPrefixUnknown] = useState(false);
    const [selectedScheme, setSelectedScheme] = useState<CreditCardScheme | ''>('');

    const {dispatch} = useNotifications();

    const scheme = useMemo(
        () => prefixUnknown
            ? (selectedScheme || CreditCardScheme.VISA)
            : detectSchemeFromCreditCardNumber(prefix),
        [prefix, prefixUnknown, selectedScheme],
    );

    const requiredPrefixLength = PREFIX_LENGTH_BY_SCHEME[scheme];

    const institutionOptions: Option[] = useMemo(
        () => [
            EMPTY_INSTITUTION_OPTION,
            ...institutions.map((institution) => ({
                value: institution.id,
                label: institution.name,
            })),
        ],
        [institutions],
    );

    const selectedInstitutionOption = institutionOptions.find(
        (option) => option.value === selectedInstitutionId,
    ) ?? EMPTY_INSTITUTION_OPTION;

    const selectedSchemeOption = SCHEME_OPTIONS.find(
        (option) => option.value === selectedScheme,
    ) ?? EMPTY_SCHEME_OPTION;

    const nameValid = name.trim().length > 0;
    const prefixValid = prefixUnknown ? selectedScheme !== '' : prefix.length === requiredPrefixLength;
    const suffixValid = suffix.length === LAST_FOUR_LENGTH;
    const submittedPrefix = prefixUnknown && selectedScheme
        ? PLACEHOLDER_PREFIX_BY_SCHEME[selectedScheme]
        : prefix;
    const isDuplicate = useMemo(
        () => prefixValid && suffixValid && existingCreditCards.some(
            (card) => card.creditCardNumberPrefix === submittedPrefix && card.creditCardNumberSuffix === suffix,
        ),
        [existingCreditCards, submittedPrefix, suffix, prefixValid, suffixValid],
    );
    const canSubmit = nameValid && prefixValid && suffixValid && !isDuplicate && !isSubmitting;

    function resetForm() {
        setName('');
        setPrefix('');
        setSuffix('');
        setSelectedInstitutionId('');
        setPrefixUnknown(false);
        setSelectedScheme('');
    }

    function handleClose() {
        resetForm();
        onClose();
    }

    async function handleSubmit() {
        if (!canSubmit) {
            return;
        }

        setIsSubmitting(true);

        try {
            await onSubmit({
                name: name.trim(),
                credit_card_number_prefix: submittedPrefix,
                credit_card_number_suffix: suffix,
                institution_name: selectedInstitutionId,
                credit_card_prefix_is_placeholder: prefixUnknown,
            });
            resetForm();
            onClose();
        } catch {
            dispatch({
                title: 'Creditcard toevoegen mislukt',
                description: 'Er ging iets mis. Probeer het opnieuw.',
                type: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    const showSchemeLogo = prefixUnknown ? selectedScheme !== '' : prefix.length > 0;

    return (
        <Dialog open={open} onClose={handleClose} className="relative z-50">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
                    >
                        <div>
                            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
                                <DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                                    Creditcard toevoegen
                                </DialogTitle>
                                <button
                                    type="button"
                                    aria-label="Sluiten"
                                    onClick={handleClose}
                                    className="rounded-md text-gray-400 hover:text-gray-500"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-4 px-6 py-4">
                                <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                                    <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                                    <span>Voeg alleen creditcards toe, geen betaalpassen of debit cards.</span>
                                </div>

                                <div>
                                    <label htmlFor="credit-card-name" className="mb-1 block text-sm font-medium text-gray-700">
                                        Naam<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <input
                                        id="credit-card-name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                        placeholder="Bijv. Zakelijke kaart"
                                    />
                                </div>

                                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={prefixUnknown}
                                        onChange={(e) => {
                                            setPrefixUnknown(e.target.checked);
                                            setPrefix('');
                                            setSelectedScheme('');
                                        }}
                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    Ik ken de eerste cijfers niet
                                </label>

                                <div className="grid grid-cols-12 items-end gap-3">
                                    <div className="col-span-5">
                                        {prefixUnknown ? (
                                            <>
                                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                                    Kaarttype<span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <Dropdown
                                                    selected={selectedSchemeOption}
                                                    options={[EMPTY_SCHEME_OPTION, ...SCHEME_OPTIONS]}
                                                    onUpdateFunc={(value) => {
                                                        const nextScheme = String(value);
                                                        setSelectedScheme(
                                                            Object.values(CreditCardScheme).includes(nextScheme as CreditCardScheme)
                                                                ? nextScheme as CreditCardScheme
                                                                : '',
                                                        );
                                                    }}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <label htmlFor="credit-card-prefix" className="mb-1 block text-sm font-medium text-gray-700">
                                                    Eerste {requiredPrefixLength} cijfers<span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <input
                                                    id="credit-card-prefix"
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete="off"
                                                    value={prefix}
                                                    maxLength={MAX_PREFIX_LENGTH}
                                                    onChange={(e) => {
                                                        const digits = stripDigits(e.target.value);
                                                        const detectedScheme = detectSchemeFromCreditCardNumber(digits);
                                                        setPrefix(digits.slice(0, PREFIX_LENGTH_BY_SCHEME[detectedScheme]));
                                                    }}
                                                    className={cn(fieldVariants(), 'tracking-widest')}
                                                    placeholder={Array(requiredPrefixLength).fill('_').join(' ')}
                                                />
                                            </>
                                        )}
                                    </div>

                                    <div className="col-span-5">
                                        <label htmlFor="credit-card-suffix" className="mb-1 block text-sm font-medium text-gray-700">
                                            Laatste {LAST_FOUR_LENGTH} cijfers<span className="text-red-500 ml-1">*</span>
                                        </label>
                                        <input
                                            id="credit-card-suffix"
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            value={suffix}
                                            maxLength={LAST_FOUR_LENGTH}
                                            onChange={(e) => setSuffix(stripDigits(e.target.value).slice(0, LAST_FOUR_LENGTH))}
                                            className={cn(fieldVariants(), 'tracking-widest')}
                                            placeholder="_ _ _ _"
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        {showSchemeLogo ? (
                                            <div
                                                className="flex h-9 items-center justify-center rounded-md bg-gray-500 px-2"
                                                aria-hidden
                                            >
                                                <CreditCardSchemeLogo scheme={scheme} size="compact" />
                                            </div>
                                        ) : (
                                            <div
                                                className="flex h-9 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 text-gray-400"
                                                aria-hidden
                                            >
                                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5}>
                                                    <rect x="2" y="5" width="20" height="14" rx="2" />
                                                    <path d="M2 10h20" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {isDuplicate && (
                                    <p className="text-sm text-red-600">
                                        Deze creditcard is al toegevoegd.
                                    </p>
                                )}

                                <div>
                                    <label htmlFor="credit-card-institution" className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700">
                                        Bank / uitgever (optioneel)
                                        <InfoTooltip position="top" text="Dit dient alleen om de bank of instelling te identificeren die de kaart heeft uitgegeven. Dit veld is optioneel." />
                                    </label>
                                    <Dropdown
                                        selected={selectedInstitutionOption}
                                        options={institutionOptions}
                                        onUpdateFunc={(value) => setSelectedInstitutionId(String(value))}
                                        customItemRenderFn={({value}) => {
                                            const institution = institutions.find((item) => item.id === value);
                                            if (!institution) {
                                                return <span>{EMPTY_INSTITUTION_OPTION.label}</span>;
                                            }
                                            return (
                                                <span className="flex items-center gap-2">
                                                    {institution.logo && (
                                                        <img
                                                            src={institution.logo}
                                                            alt=""
                                                            className="h-5 w-5 rounded-full object-contain"
                                                        />
                                                    )}
                                                    <span>{institution.name}</span>
                                                </span>
                                            );
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
                                <SecondaryButton title="Annuleren" onClick={handleClose} />
                                <PrimaryButton
                                    title="Toevoegen"
                                    onClick={handleSubmit}
                                    disabled={!canSubmit}
                                    disabledFeedback="Vul alle velden in"
                                    confirmation={{
                                        title: 'Creditcard toevoegen',
                                        description: 'Weet je zeker dat je deze creditcard wilt toevoegen? Voeg alleen creditcards toe, geen betaalpassen of debit cards.',
                                        acceptVerb: 'Toevoegen',
                                    }}
                                />
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
